/* eslint-disable react-hooks/rules-of-hooks */
import { Text, useToast, VStack } from '@chakra-ui/react';
import { PatchDataWrapper } from '@diamond/shared/data-access';
import {
  BackofficeActivitiesOrderItem,
  BackofficeActivitiesOrderItems,
  BackofficeOrderUpdate,
  BackofficeRevalidateUpdate,
} from '@diamond/shared/types';
import { QueryKey } from '@tanstack/react-query';
import { CellContext, createColumnHelper, Row } from '@tanstack/react-table';

import { useActivityDetail } from '../hooks/use-activity-detail';
import {
  formatCurrency,
  getActivityItemDisplay,
  getOrderItemDisplaySubtotal,
  resolveActivityCheckoutDisplaySubtotal,
} from '../utils/activity-formatters';
import { ActivityDetailDiffLabel } from './activity-detail/activity-detail-stage-qty-cell';
import {
  CancelReasonColumn,
  CheckboxColumn,
  CheckboxHeader,
  ConfirmedQuantityColumn,
  DeliveredQuantityColumn,
  ImageColumn,
  IsNotProcessedColumn,
  IsUpdatedColumn,
  ItemCodeColumn,
  LatestPriceColumn,
  NoteColumn,
  PriceColumn,
  ProcessedQuantityColumn,
  ProductNameColumn,
  QuantityColumn,
  ReceivedQuantityColumn,
  StorageTypeColumn,
  ValuationTypeColumn,
} from './activity-details-columns';

const columnHelper = createColumnHelper<BackofficeActivitiesOrderItems[0]>();

const API_URL = 'backoffice/order';

/**
 * Creates info columns for the activity detail table
 * @deprecated Use ItemCodeColumn, ImageColumn, ProductNameColumn, QuantityColumn from './activity-details-columns' instead
 */
export function createInfoColumns() {
  return [
    columnHelper.accessor('item_code', {
      cell: (info) => <ItemCodeColumn info={info} />,
      header: 'Kode Item',
    }),
    columnHelper.accessor('images_medium', {
      cell: (info) => <ImageColumn info={info} />,
      header: 'Gambar',
    }),
    columnHelper.accessor('title', {
      cell: (info) => <ProductNameColumn info={info} />,
      header: 'Nama Produk',
    }),
    columnHelper.accessor('quantity', {
      cell: (info) => (
        <QuantityColumn
          info={
            info as CellContext<
              BackofficeActivitiesOrderItem,
              number | undefined
            >
          }
          variant="checkout"
        />
      ),
      header: 'Jumlah Checkout',
    }),
  ];
}

/**
 * @deprecated Use createInfoColumns() instead
 * Use individual column components from './activity-details-columns' for new code
 */
export const infoColumns = createInfoColumns();

/**
 * @deprecated Use ProcessedQuantityColumn, DeliveredQuantityColumn, ReceivedQuantityColumn from './activity-details-columns'
 */
export const quantityColumns = [
  columnHelper.accessor('processed_quantity', {
    cell: (info) => <ProcessedQuantityColumn info={info} />,
    header: 'Diproses',
  }),
  columnHelper.accessor('delivered_quantity', {
    cell: (info) => <DeliveredQuantityColumn info={info} />,
    header: 'Dikirim',
  }),
  columnHelper.accessor('received_quantity', {
    cell: (info) => <ReceivedQuantityColumn info={info} />,
    header: 'Diterima',
  }),
];

type BackOfficeCustomBadge = {
  is_new_product: boolean;
};

type CustomColumnType = BackofficeActivitiesOrderItem & BackOfficeCustomBadge;

/**
 * @deprecated Use individual column components from './activity-details-columns' instead
 * This hook creates editable columns for the activity detail table
 * For new code, import and use individual column components directly
 */
export function useEditableColumns(
  setSelectedData: (value: Row<BackofficeActivitiesOrderItem>[]) => void,
  selectedData: Row<BackofficeActivitiesOrderItem>[],
  refetch: () => void,
  activitiesId: string,
  cancelReasonData: { label: string; value: string }[],
  isDisable: boolean,
  queryKey: QueryKey = [],
  token?: string,
  toast?: ReturnType<typeof useToast>,
  isVoucherEnabled = false
) {
  const activityDetail = useActivityDetail({
    selectedData,
    setSelectedData,
    refetch,
    activitiesId,
    queryKey,
    isVoucherEnabled,
    token,
    toast,
  });

  const handleSelectAll = (values: any[], isChecked: boolean) => {
    activityDetail.handleSelectAll(values, isChecked);
  };

  const handleSelectOne = (value: any, isChecked: boolean) => {
    activityDetail.handleSelectOneRow(value, isChecked);
  };

  const handleUpdate = (name: string, value: unknown, itemId: string) => {
    const { patchData } = PatchDataWrapper<
      BackofficeOrderUpdate & BackofficeRevalidateUpdate
    >(API_URL, token as string, 'admin');

    patchData({ [name]: value }, '', `${activitiesId}/item/${itemId}`).then(
      () => {
        refetch();
      }
    );
  };

  const checkboxColumn = [
    {
      id: 'select',
      header: ({ table }: any) => (
        <CheckboxHeader
          table={table}
          onSelectAll={handleSelectAll}
          isDisabled={isDisable}
        />
      ),
      cell: ({ row }: any) => (
        <CheckboxColumn
          info={row}
          selectedData={selectedData}
          onSelectAll={handleSelectAll}
          onSelectOne={handleSelectOne}
          isDisabled={isDisable}
        />
      ),
    },
  ];

  const editableColumns = [
    columnHelper.accessor('confirmed_quantity', {
      cell: (info) => {
        const display = getActivityItemDisplay(info.row.original);

        return (
          <VStack align="flex-start" spacing={0}>
            <ConfirmedQuantityColumn
              info={
                info as CellContext<
                  BackofficeActivitiesOrderItem,
                  number | undefined
                >
              }
              selectedData={selectedData}
              onUpdate={(value, itemId) =>
                handleUpdate('confirmed_quantity', value, itemId)
              }
              queryKey={queryKey}
              activitiesId={activitiesId}
              token={token}
              refetch={refetch}
            />
            {display.showInitialQuantity ? (
              <ActivityDetailDiffLabel
                label="Pesanan Awal"
                value={String(display.checkoutQuantity)}
                align="left"
              />
            ) : null}
          </VStack>
        );
      },
      header: 'Qty. Dikonfirmasi',
    }),
    columnHelper.accessor('price', {
      cell: (info) => (
        <PriceColumn info={info} isVoucherEnabled={isVoucherEnabled} />
      ),
      header: 'Harga Checkout',
    }),
    columnHelper.accessor('latest_price', {
      cell: (info) => (
        <LatestPriceColumn
          info={info}
          isVoucherEnabled={isVoucherEnabled}
          selectedData={selectedData}
          onUpdate={(value, itemId) =>
            handleUpdate('latest_price', value, itemId)
          }
          queryKey={queryKey}
          activitiesId={activitiesId}
          token={token}
          refetch={refetch}
        />
      ),
      header: 'Harga Akhir',
    }),
    columnHelper.accessor('subtotal', {
      cell: (info) => {
        const item = info.row.original;
        const display = getActivityItemDisplay(item);

        if (!display.showPriceSection) {
          return null;
        }

        const lineSubtotal = getOrderItemDisplaySubtotal(item);

        if (display.showInitialTotalPrice) {
          return (
            <VStack align="flex-start" spacing={0}>
              <Text fontSize="sm" fontWeight="400" color="#373F41">
                {formatCurrency(lineSubtotal)}
              </Text>
              <ActivityDetailDiffLabel
                label="total awal"
                value={formatCurrency(display.initialTotalPrice)}
                align="left"
              />
            </VStack>
          );
        }

        const checkoutSubtotal = resolveActivityCheckoutDisplaySubtotal(item);

        if (checkoutSubtotal == null) {
          return '-';
        }

        return (
          <VStack align="flex-start" spacing={0}>
            <Text fontSize="sm" fontWeight="400" color="#373F41">
              {formatCurrency(checkoutSubtotal)}
            </Text>
          </VStack>
        );
      },
      header: 'Total Harga',
    }),
    columnHelper.accessor('note', {
      cell: (info) => (
        <NoteColumn
          info={info}
          selectedData={selectedData}
          onUpdate={(value, itemId) => handleUpdate('note', value, itemId)}
          queryKey={queryKey}
          activitiesId={activitiesId}
          token={token}
          refetch={refetch}
        />
      ),
      header: 'Catatan Produk',
    }),
    columnHelper.accessor('is_not_processed', {
      cell: (info) => (
        <IsNotProcessedColumn
          info={info}
          selectedData={selectedData}
          onUpdate={(value, itemId) =>
            handleUpdate('is_not_processed', value, itemId)
          }
          activitiesId={activitiesId}
          token={token}
          refetch={refetch}
        />
      ),
      header: 'Tidak Diproses',
    }),
    columnHelper.accessor('is_updated', {
      cell: (info) => <IsUpdatedColumn info={info} />,
      header: 'Pernah Diubah',
    }),
    columnHelper.accessor('product_storage', {
      cell: (info) => (
        <StorageTypeColumn
          info={info}
          selectedData={selectedData}
          onUpdate={(value, itemId) =>
            handleUpdate('product_storage', value, itemId)
          }
          activitiesId={activitiesId}
          token={token}
          refetch={refetch}
        />
      ),
      header: 'Tempat Penyimpanan',
    }),
    columnHelper.accessor('valuation_type', {
      cell: (info) => (
        <ValuationTypeColumn
          info={info}
          selectedData={selectedData}
          onUpdate={(value, itemId) =>
            handleUpdate('valuation_type', value, itemId)
          }
          activitiesId={activitiesId}
          token={token}
          refetch={refetch}
        />
      ),
      header: 'Valuation Type',
    }),
    columnHelper.accessor('cancel_reason_id', {
      cell: (info) => (
        <CancelReasonColumn
          info={
            info as CellContext<
              BackofficeActivitiesOrderItem,
              string | null | undefined
            >
          }
          selectedData={selectedData}
          onUpdate={(value, itemId) =>
            handleUpdate('cancel_reason_id', value, itemId)
          }
          activitiesId={activitiesId}
          token={token}
          refetch={refetch}
          cancelReasonData={cancelReasonData}
        />
      ),
      header: 'Alasan Pembatalan',
    }),
  ];

  return { checkboxColumn, editableColumns };
}

/**
 * @deprecated Use individual column components from './activity-details-columns' instead
 * This export is maintained for backward compatibility only.
 * See: /libs/sol-admin/features/activity/src/lib/components/activity-details-columns/README.md
 */
export const deprecatedExports = {
  createInfoColumns,
  infoColumns,
  quantityColumns,
  useEditableColumns,
};
